import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useApiErrorHandler } from "../lib/session";
import type { SearchResult } from "../lib/types";
import { Notice, placeTypeLabel } from "./bits";

// Nominatim allows one request per second, and the backend enforces it globally.
// Firing on every keystroke would queue requests behind that lock and make the
// field feel broken, so wait for a pause in typing instead.
const DEBOUNCE_MS = 550;
const MIN_QUERY = 2;

export function PlaceSearch({
  onPick,
  autoFocus,
}: {
  onPick: (result: SearchResult) => void;
  autoFocus?: boolean;
}) {
  const handleError = useApiErrorHandler();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Clearing and the spinner follow the typing directly; the effect only runs the
  // debounced request.
  function changeQuery(next: string) {
    setQuery(next);
    if (next.trim().length < MIN_QUERY) {
      setResults(null);
      setSearching(false);
    } else {
      setSearching(true);
    }
  }

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) return;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const found = await api.searchPlaces(trimmed);
        // A slow earlier request must not overwrite a newer query's results.
        if (!cancelled) {
          setResults(found);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(handleError(err));
          setResults(null);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, handleError]);

  return (
    <div className="stack">
      <div className="field">
        <label htmlFor="place-search">Ortsname</label>
        <input
          id="place-search"
          ref={inputRef}
          className="input"
          value={query}
          onChange={(e) => changeQuery(e.target.value)}
          placeholder="Porto, Ravenglass, Zermatt…"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          enterKeyHint="search"
          type="search"
        />
      </div>

      {error ? <Notice tone="error">{error}</Notice> : null}

      {searching ? (
        <p className="row tiny muted" style={{ gap: 8 }}>
          <span className="spin" aria-hidden style={{ width: 14, height: 14 }} />
          Suche läuft
        </p>
      ) : null}

      {results && results.length === 0 && !searching ? (
        <p className="notice muted">
          Nichts gefunden für „{query.trim()}“. Conquest kennt nur Städte, Dörfer
          und Weiler — versuch den nächstgelegenen Ort.
        </p>
      ) : null}

      {results && results.length > 0 ? (
        <ul className="results" aria-label="Suchergebnisse">
          {results.map((result) => (
            <li key={result.osm_id}>
              <button className="result" onClick={() => onPick(result)}>
                <span className="row spread" style={{ alignItems: "baseline", gap: 8 }}>
                  <strong className="result__name">{result.name}</strong>
                  {result.existing ? (
                    <span className={result.existing.conquered ? "pill pill--won" : "pill"}>
                      {result.existing.conquered
                        ? "Erobert"
                        : `${result.existing.earned_points}/${result.existing.target_points}`}
                    </span>
                  ) : null}
                </span>
                <span className="result__where">{result.display_name}</span>
                <span className="tiny muted">{placeTypeLabel(result.place_type)}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
